import { Component, inject, OnInit } from '@angular/core';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreId,
  extractNumber,
  extractNumberArr,
  HxReportService,
  HxUiLabelPipe,
  InventoryManagementModel,
  isoDate,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxProductCategorySelectComponent, HxStoreSelectComponent } from 'hx-component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { saveAs } from 'file-saver';
import { HcToastrService } from '@manager-app/service/toastr.service';

@Component({
  selector: 'app-inventory-management',
  templateUrl: './inventory-management.component.html',
  imports: [
    HxStoreSelectComponent,
    TranslocoPipe,
    FormsModule,
    HxDateRangeSelectComponent,
    HxProductCategorySelectComponent,
    NgIf,
    HxLoaderComponent,
    HxUiLabelPipe,
    NgClass,
    RouterLink,
  ],
  styleUrls: ['./inventory-management.component.css']
})
export class InventoryManagementComponent implements OnInit {
  params: {
    storeId?: number,
    date?: DateRange,
    categoryIds?: number[];
  } = {};
  isLoading = {
    list: false,
    generate: false,
  };
  brandId?: number;
  list: InventoryManagementModel[] = [];

  private readonly reportService = inject(HxReportService);
  private readonly aRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(HcToastrService);
  private readonly tr = inject(TranslocoService);

  constructor() {
    this.aRoute.queryParamMap.pipe(takeUntilDestroyed()).subscribe(async paramMap => {
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.categoryIds = extractNumberArr('categoryIds', paramMap, [1]);
      this.params.storeId = extractNumber('storeId', paramMap) ?? extractDefaultStoreId();
      if (this.params.storeId && this.params.categoryIds) {
        this.load();
      }
    });
  }

  ngOnInit() {
    this.brandId = localStorage.getItem('defaultBrandId') ? Number(localStorage.getItem('defaultBrandId')) : undefined;
  }

  resetFilter() {
    this.params.date = {from: isoDate(new Date()), to: isoDate(new Date())};
    this.navigate();
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (Array.isArray(store)) {
      return;
    }
    this.brandId = store?.brandId;
    this.navigate();
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  async generate() {
    this.isLoading.generate = true;
    try {
      const response = await this.reportService.generateInventoryManagement({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeId: this.params.storeId,
        categoryIds: this.params.categoryIds
      });
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.toastr.success(this.tr.translate('received-report.ts.success'));
    } catch (e: any) {
      console.log(e);
    } finally {
      this.isLoading.generate = false;
    }
  }

  async load() {
    this.list = [];
    this.isLoading.list = true;
    try {
      this.list = await this.reportService.getInventoryManagement({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeId: this.params.storeId,
        categoryIds: this.params.categoryIds
      });
    } finally {
      this.isLoading.list = false;
    }
  }
}
